import type { ReactNode } from "react";

import { PlusCircleIcon } from "../../icons/plus-circle";
import { Button } from "../button";

import styles from "./empty-state.module.css";

type Domain =
	| "ACCOUNTS"
	| "BATCHES"
	| "BENEFICIARIES"
	| "CLOSED_ACCOUNTS"
	| "CUSTOMERS"
	| "PENDING_ACCOUNTS"
	| "REQUEST_BATCHES"
	| "REQUESTS"
	| "TRANSACTIONS"
	| "TREASURY_SERVICES_CLOSED_POSITIONS"
	| "TREASURY_SERVICES_HISTORY"
	| "TREASURY_SERVICES_INSTRUMENTS"
	| "TREASURY_SERVICES_OPEN_POSITIONS";

type EmptyStateProps = {
	customSubtitle?: ReactNode;
	domain: Domain;
	showButton: boolean;
	showPlusIcon?: boolean;
};

const TITLE: Record<Domain, string> = {
	ACCOUNTS: "No accounts found",
	BATCHES: "No batch payments found",
	BENEFICIARIES: "No beneficiaries found",
	CLOSED_ACCOUNTS: "No closed accounts found",
	CUSTOMERS: "No customers found",
	PENDING_ACCOUNTS: "No pending accounts found",
	REQUEST_BATCHES: "No batch payment found",
	REQUESTS: "No requests found",
	TRANSACTIONS: "No transactions found",
	TREASURY_SERVICES_CLOSED_POSITIONS: "No closed positions",
	TREASURY_SERVICES_HISTORY: "No logs found",
	TREASURY_SERVICES_INSTRUMENTS: "No instruments available",
	TREASURY_SERVICES_OPEN_POSITIONS: "No open positions",
};

const BODY: Record<Domain, string> = {
	ACCOUNTS: "Once an account is created, it will appear here.",
	BATCHES: "",
	BENEFICIARIES: "Once a beneficiary is added, it will appear here.",
	CLOSED_ACCOUNTS: "Closed accounts will appear here.",
	CUSTOMERS: "Once a customer is created, it will appear here.",
	PENDING_ACCOUNTS: "Pending accounts will appear here.",
	REQUEST_BATCHES: "",
	REQUESTS: "Once a request is submitted, it will appear here.",
	TRANSACTIONS: "Once a transaction is made, it will appear here.",
	TREASURY_SERVICES_CLOSED_POSITIONS: "Once you reedem a position it will appear here.",
	TREASURY_SERVICES_HISTORY: "Any operations made in treasury services will appear here.",
	TREASURY_SERVICES_INSTRUMENTS: "Tradable instruments will appear here once available.",
	TREASURY_SERVICES_OPEN_POSITIONS:
		"Once you invest in an instrument, your position will appear here.",
};

const BUTTON_TEXT: Record<Domain, string> = {
	ACCOUNTS: "Create account",
	BATCHES: "New batch payment",
	BENEFICIARIES: "New beneficiary",
	CLOSED_ACCOUNTS: "",
	CUSTOMERS: "New customer",
	PENDING_ACCOUNTS: "",
	REQUEST_BATCHES: "Request batch payment",
	REQUESTS: "New request",
	TRANSACTIONS: "New transaction",
	TREASURY_SERVICES_CLOSED_POSITIONS: "Browse instruments",
	TREASURY_SERVICES_HISTORY: "Manage portfolio",
	TREASURY_SERVICES_INSTRUMENTS: "",
	TREASURY_SERVICES_OPEN_POSITIONS: "Browse instruments",
};

const BUTTON_HREF: Record<Domain, string> = {
	ACCOUNTS: "/accounts/create",
	BATCHES: "/batch-payments/new",
	BENEFICIARIES: "/beneficiaries/create",
	CLOSED_ACCOUNTS: "",
	CUSTOMERS: "/customers/create",
	PENDING_ACCOUNTS: "",
	REQUEST_BATCHES: "/requests/new/batch-payment",
	REQUESTS: "/requests/new",
	TRANSACTIONS: "/transactions/new",
	TREASURY_SERVICES_CLOSED_POSITIONS: "/treasury-services/instruments",
	TREASURY_SERVICES_HISTORY: "/treasury-services/flow/manage",
	TREASURY_SERVICES_INSTRUMENTS: "",
	TREASURY_SERVICES_OPEN_POSITIONS: "/treasury-services/instruments",
};

export function EmptyState({
	customSubtitle,
	domain,
	showButton,
	showPlusIcon = true,
}: EmptyStateProps): ReactNode {
	return (
		<div className={styles.wrapper}>
			<div className={styles.content}>
				<h2 className="title-2">{TITLE[domain]}</h2>
				{customSubtitle ? customSubtitle : <p className="body-1">{BODY[domain]}</p>}
				<>
					{showButton && BUTTON_TEXT[domain] && (
						<Button
							className={styles.button}
							href={BUTTON_HREF[domain]}
							variant="primary"
						>
							{showPlusIcon && <PlusCircleIcon height={16} width={16} />}
							{BUTTON_TEXT[domain]}
						</Button>
					)}
				</>
			</div>
		</div>
	);
}
